/**
 * Project records: the one store behind the project's shared things and a person's own things in
 * a project (docs/plans/2026-10-project-person-machine-platform.md, D215). A **record** is
 * `{ id, kind, scope, principal?, version, createdAt, updatedAt, updatedBy, label?, data }`; a
 * record may have an append-only, server-numbered stream of **entries** (messages, events).
 * AIOE's own contract; the workbench copies it into `shared/schemas/projectRecords.ts`.
 *
 * Scope `project`: every member reads (viewers too); contributors and owners write; every write is
 * audited under the project with the person as principal and the agent as actor, and goes to the
 * project stream as a `record` event. Scope `person`: only that person reads or writes (their
 * sign-in, their desktop's token, or a node naming them); nothing is streamed; an administrator
 * may purge a person's records, never read them.
 *
 * Writes carry `If-Match: "<version>"` and answer 412 `{ current }` when stale. Ids are the
 * client's, so `PUT` creates or replaces and a one-time upload is idempotent. Limits: `data` at
 * most 2 MB; an entry at most 200 KB; 20 000 entries per record.
 *
 * Round 1 (this file) serves two kinds and one more route:
 *
 *   GET|PUT /projects/v1/:id/settings-layer              { revision, settings, updatedAt?, updatedBy? } + ETag "<revision>"
 *                                                         owners write (a sign-in, or their desktop's token); members and their machines read
 *   GET     /projects/v1/:id/saved-teams                  members: { teams: SavedTeam[] } — the teams saved for this project
 *   PUT     /projects/v1/:id/saved-teams/:teamId          contributors and above: the workbench's TeamConfig (checked: id, kind "team", name,
 *                                                         members[]; a template's placement rules apply); If-Match; → the saved team + ETag
 *   DELETE  /projects/v1/:id/saved-teams/:teamId          contributors and above → 204
 *   DELETE  /projects/v1/:id/roots/:rootId                owners: every clone record of the root goes (desktops' and nodes'); the project's
 *                                                         boards, runs, plans and risks for it stay readable; `root.removed` on the stream
 *
 * Words for people: saved for this project, the project's settings, removed from the project.
 */
import { z } from "zod";
export const PROJECT_RECORDS_SCHEMA_VERSION = 1;
export const RECORD_DATA_MAX_BYTES = 2_000_000;
export const RECORD_ENTRY_MAX_BYTES = 200_000;
export const RECORD_ENTRIES_MAX = 20_000;
export const recordScopeSchema = z.enum(["project", "person"]);
const idSchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.:-]+$/, "letters, digits and _ . : -");
export const recordBySchema = z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional(), deviceId: z.string().max(100).optional() });
/** A sensitivity label a record carries (a person's conversation: the highest its session held). */
export const recordLabelSchema = z.object({ sensitivity: z.enum(["public", "internal", "confidential", "restricted"]), compartments: z.array(z.string().max(100)).max(50).optional() });
// ── The project's settings layer ───────────────────────────────────────────
export const settingsLayerPutSchema = z.object({
    /** The project's own setting values (the workbench's declared keys), beneath the organisation's governed ones. */
    settings: z.record(z.string(), z.unknown()),
});
// ── Teams saved for the project ────────────────────────────────────────────
/** The workbench's TeamConfig as it is saved: the fields the platform reads are checked, the rest is carried as written. */
export const savedTeamPutSchema = z.looseObject({
    id: idSchema,
    kind: z.literal("team").default("team"),
    name: z.string().min(1).max(200),
    members: z.array(z.looseObject({ name: z.string().min(1).max(100) })).max(50),
});
//# sourceMappingURL=projectRecords.js.map